<template>
  <EditorShell v-bind="{ config, lotteryId, loaded, navigateToHome, optionsReady }">
    <template v-if="config && priceList">
      <EditorPlayList v-bind="{ animated, config, store }">
        <template #default="{ play }">
          <EditorPickLotteryPicker v-bind="{ config, lotteryId, play, priceList }" />
        </template>
      </EditorPlayList>

      <EditorDeleteAllPlays :visible="canDeleteAllPlays" @confirm="deleteAllPlays" />
    </template>

    <template #control-panel>
      <EditorControlPanel v-bind="{ actions, disabled }" @click="handleAction" />
    </template>
  </EditorShell>
</template>

<script lang="ts" setup>
import type { Config, ControlPanelAction } from '../@types/editor'

import { EDITOR_CONTROL_PANEL_ACTION_ID } from '../constants/editor'
import { usePickLotteryStore } from '../stores/pickLottery'
import EditorPickLotteryQuickPickModal from './PickLotteryQuickPickModal.vue'

const SINGLE_QUICK_PICK = 1

const { registerModal } = useModal()
const { t } = useI18n()
const {
  config,
  editorIsReady,
  lotteryId,
  navigateToHome,
  optionsReady,
  playsReady,
  priceList,
  store,
} = useEditor(usePickLotteryStore)
const { addLine, addLineDisabled, disabled, scrollToCenteredPlay } = useEditorControlPanel(
  store,
  config,
)
const { applyQuickPickFromQuery, quickPickReady } = useEditorQuickPickQuery(
  store,
  config,
  priceList,
)
const { animated, canDeleteAllPlays, deleteAllPlays } = useDeleteAllPlays(store, config)
const { triggerBatch } = useEditorQuickPickAnimation()

useEditorMaxPlaysToast(store, config, disabled)

const isModalVisible = ref(false)

const loaded = computed(
  () => !!(config.value && priceList.value) && playsReady.value && quickPickReady.value,
)

const actions = computed<ControlPanelAction[]>(() => [
  {
    disabled: addLineDisabled.value,
    icon: 'plus',
    id: EDITOR_CONTROL_PANEL_ACTION_ID.AddLine,
    label: t('editor.controlpanel.button.addline'),
    variant: 'fade',
  },
  quickPickAction.value,
])

const isFillTarget = computed(() => {
  const currentConfig = config.value
  return !!currentConfig && store.isQuickPickFillTarget(currentConfig)
})

const maxQuantity = computed(() => {
  const currentConfig = config.value
  if (!currentConfig) return 0

  return store.remainingQuickPickSlots(currentConfig)
})

const quickPickAction = computed<ControlPanelAction>(() => ({
  icon: isFillTarget.value ? 'flash' : 'flash-plus',
  id: EDITOR_CONTROL_PANEL_ACTION_ID.QuickPick,
  label: t('editor.controlpanel.button.quickpick'),
  variant: 'prominent',
}))

const quickPickModalProps = computed(() => {
  const currentConfig = config.value
  const currentPriceList = priceList.value
  if (!currentConfig || !currentPriceList) return null

  return {
    config: currentConfig,
    lotteryId,
    maxQuantity: maxQuantity.value,
    priceList: currentPriceList,
  }
})

const runQuickPick = async (quickPick: () => string[], currentConfig: Config) => {
  const playIds = triggerBatch({
    config: currentConfig,
    expandedPlayId: store.expandedPlayId,
    plays: () => store.plays,
    quickPick,
  })

  await scrollToCenteredPlay(playIds.at(-1) ?? null)
}

const handleAction = async (actionId: string) => {
  const currentConfig = config.value
  if (!currentConfig) return

  if (actionId === EDITOR_CONTROL_PANEL_ACTION_ID.AddLine) return addLine()
  if (actionId !== EDITOR_CONTROL_PANEL_ACTION_ID.QuickPick) return

  if (!isFillTarget.value) {
    isModalVisible.value = true
    return
  }

  await runQuickPick(() => store.quickPickPlays(currentConfig, SINGLE_QUICK_PICK), currentConfig)
}

const closeQuickPickModal = () => {
  isModalVisible.value = false
}

const handleConfirm = async () => {
  const currentConfig = config.value
  if (!currentConfig) return

  closeQuickPickModal()
  await runQuickPick(
    () => store.quickPickPlaysWithSettings(currentConfig, store.quickPickSettings),
    currentConfig,
  )
}

onMounted(async () => {
  await editorIsReady
  await applyQuickPickFromQuery()
})

registerModal({
  component: EditorPickLotteryQuickPickModal,
  onClose: closeQuickPickModal,
  onConfirm: handleConfirm,
  props: () => quickPickModalProps.value,
  stack: true,
  when: () => isModalVisible.value,
})
</script>
